/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */

import React, { useCallback, useState } from 'react';

import { css, jsx } from '@compiled/react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { Popup } from '@atlaskit/editor-common/ui';
import type { StreamingCommand } from '@atlassian/editor-plugin-ai-streaming-orchestrator';

import type { AiSuggestionsPlugin } from '../../../aiSuggestionsPluginType';
import { aiSuggestionsPluginKey } from '../../../pm-plugins/main';

import { SuggestionCard } from './SuggestionCard';

const containerStyle = css({
	display: 'block',
	position: 'absolute',
	zIndex: 2,
});

/**
 * We render this in a content component because rendering directly in a Decoration widget
 * will cause it to inherit font stylings from Editor which is not appropriate for this component.
 */
export const SuggestionCardContentComponent = ({
	api,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
}) => {
	/**
	 * We use useState & useCallback to set the ref so that the popup will mount correctly after the
	 * anchor mounts.
	 */
	const [target, setTarget] = useState<HTMLSpanElement>();
	const targetRef = useCallback((node: HTMLSpanElement) => {
		setTarget(node);
	}, []);
	const { selectedSuggestion } = useSharedPluginStateWithSelector(
		api,
		['aiSuggestions'],
		(state) => ({
			selectedSuggestion: state?.aiSuggestionsState?.suggestions.find(
				(s) => s.id === state?.aiSuggestionsState?.selectedSuggestion,
			),
		}),
	);

	if (selectedSuggestion === undefined) {
		return;
	}

	const onClose = () => {
		api?.core?.actions.execute(({ tr }) => {
			tr.setMeta(aiSuggestionsPluginKey, {
				action: 'SELECT_SUGGESTION',
				id: undefined,
			});
			api?.showDiff?.commands.hideDiff?.({ tr });

			return tr;
		});
	};

	const onDiscard = () => {
		api?.core?.actions.execute(({ tr }) => {
			tr.setMeta(aiSuggestionsPluginKey, {
				action: 'REMOVE_SUGGESTION',
				id: selectedSuggestion.id,
			});

			api?.showDiff?.commands.hideDiff?.({ tr });

			return tr;
		});
	};

	const onAccept = () => {
		api?.core?.actions.execute(({ tr }) => {
			api?.showDiff?.commands.hideDiff?.({ tr });
			tr.setMeta(aiSuggestionsPluginKey, {
				action: 'REMOVE_SUGGESTION',
				id: selectedSuggestion.id,
			});
			return tr;
		});

		setTimeout(() => {
			api?.core.actions.execute(({ tr }) => {
				api?.aiStreamingOrchestrator?.actions.startSession();
				selectedSuggestion.toolCalls.forEach((toolCall) => {
					const id = api?.aiStreamingOrchestrator?.actions.initAction(
						{
							type: toolCall.name,
							localId: toolCall.arguments.localId,
						} as StreamingCommand,
						{
							hideLoadingScreen: true,
						},
					);
					if (id) {
						api?.aiStreamingOrchestrator?.actions.streamValue(id, toolCall.arguments.value);
						api?.aiStreamingOrchestrator?.actions.finishAction(id, undefined);
					}
				});
				api?.aiStreamingOrchestrator?.actions.endSession();
				return tr;
			});
		}, 0);
	};
	/**
	 * We create an anchor that is positioned to match the anchor created in Decoration.widget.
	 * We also use a Popup and mount it onto this anchor so that we get the benefits of
	 * boundary detection and easier positioning.
	 */
	return (
		<>
			<span
				ref={targetRef}
				css={containerStyle}
				style={{
					positionAnchor: `--button-${selectedSuggestion.id}`,
					insetBlockStart: `anchor(start)`,
					insetInlineStart: `anchor(end)`,
				}}
			/>

			<Popup target={target} stick={true} minPopupMargin={24}>
				<SuggestionCard
					suggestion={selectedSuggestion?.description}
					onClose={onClose}
					onDiscard={onDiscard}
					onAccept={onAccept}
				/>
			</Popup>
		</>
	);
};
